import { Translator } from 'Translator';
import Load from 'components/Load';
import LoadingDots from 'components/LoadingDots';
import LoggedOut from 'components/environments/LoggedOut';
import NavigationableLayout from 'modules/app/components/NavigationableLayout';
import Notfound from 'modules/errorpages/Notfound';
import AgendaBottomBar from 'modules/host-agenda/layout/AgendaBottomBar';
import React from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import basePath from 'utils/basePath';
import { useUser } from '~/authentication/store';

const AsyncOnboarding = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-onboarding" */ 'modules/host-agenda/pages/OnboardingOverview'
    ),
);

const AsyncMonthView = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/MonthOverview'
    ),
);
const AsyncWeekView = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/WeekOverview'
    ),
);
const AsyncDayView = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/DayOverview'
    ),
);

const AsyncCreateEvent = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-create-event" */ 'modules/host-agenda/pages/CreateEventForm'
    ),
);
const AsyncUpdateEvent = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-update-event" */ 'modules/host-agenda/pages/UpdateEventForm'
    ),
);
const AsyncAgendaEventNotFound = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-not-found" */ 'modules/host-agenda/pages/AgendaEventNotFound'
    ),
);
const AsyncAgendaEventCreated = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-action-confirmation" */ 'modules/host-agenda/pages/AgendaEventCreated'
    ),
);
const AsyncAgendaEventUpdated = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-action-confirmation" */ 'modules/host-agenda/pages/AgendaEventUpdated'
    ),
);
const AsyncAgendaEventRemoved = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-action-confirmation" */ 'modules/host-agenda/pages/AgendaEventRemoved'
    ),
);

const AsyncExceptionSettings = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-exceptions" */ 'modules/host-agenda/pages/BookAheadTimeException'
    ),
);
const AsyncBookAheadTimePage = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda-exceptions" */ 'modules/host-agenda/pages/BookAheadTimeOverview'
    ),
);

const AsyncSettingsPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-agenda" */ 'modules/host-agenda/pages/SettingsOverview'
    ),
);

const SubRoutes = () => {
  const { path } = useRouteMatch();
  const { isAdmin, isHost, isLoggedIn, loading } = useUser();

  if (!loading && !isLoggedIn) {
    return <LoggedOut title={'Login to view this content'} />;
  }

  if (loading) {
    return <LoadingDots />;
  }

  if (!isHost && !isAdmin) {
    return (
      <Redirect
        to={generatePathnameWithLangCode('/', Translator.getLanguageCode())}
      />
    );
  }

  return (
    <Switch>
      <NavigationableLayout
        key={'onboarding'}
        path={`${path}/onboarding`}
        component={AsyncOnboarding}
        hideFooter
        noNavbar
        showBottomNavigation
        bottomBar={<AgendaBottomBar />}
      />
      <NavigationableLayout
        key={'onboarding'}
        path={`${path}/onboarding/:slug`}
        component={AsyncOnboarding}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'create-event'}
        path={`${path}/create-event/:start?/:end?`}
        component={AsyncCreateEvent}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'update-event'}
        path={`${path}/update-event/:id`}
        component={AsyncUpdateEvent}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'event-not-found'}
        path={`${path}/event-not-found`}
        component={AsyncAgendaEventNotFound}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'event-created'}
        path={`${path}/event-created`}
        component={AsyncAgendaEventCreated}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'event-updated'}
        path={`${path}/event-updated`}
        component={AsyncAgendaEventUpdated}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'event-removed'}
        path={`${path}/event-removed`}
        component={AsyncAgendaEventRemoved}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'month'}
        path={`${path}/month`}
        component={AsyncMonthView}
        hideFooter
        noNavbar
        showBottomNavigation
        bottomBar={<AgendaBottomBar />}
      />
      <NavigationableLayout
        key={'month'}
        path={`${path}/week/:year/:weekNumber`}
        component={AsyncWeekView}
        hideFooter
        noNavbar
        showBottomNavigation
        bottomBar={<AgendaBottomBar />}
      />
      <NavigationableLayout
        key={'day'}
        path={`${path}/day/:date`}
        component={AsyncDayView}
        hideFooter
        noNavbar
      />
      <Redirect
        from={`${path}/settings/exception/:id`}
        to={`${path}/settings/book-ahead-time/exception/:id`}
      />
      <NavigationableLayout
        key={'settings-exception'}
        path={`${path}/settings/book-ahead-time/exception/:id`}
        component={AsyncExceptionSettings}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'book-ahead-time'}
        path={`${path}/settings/book-ahead-time`}
        component={AsyncBookAheadTimePage}
        hideFooter
        noNavbar
      />
      <NavigationableLayout
        key={'settings'}
        path={`${path}/settings`}
        component={AsyncSettingsPage}
        hideFooter
        noNavbar
        showBottomNavigation
        bottomBar={<AgendaBottomBar />}
      />
      <NavigationableLayout
        key={'host-agenda'}
        path={`${path}/`}
        component={AsyncMonthView}
        hideFooter
        noNavbar
        showBottomNavigation
        bottomBar={<AgendaBottomBar />}
      />
      <Route>
        <Notfound />
      </Route>
    </Switch>
  );
};

const routes = (
  <Route path={`${basePath}/host-agenda`}>
    <SubRoutes />
  </Route>
);

export default routes;
